import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { authLog } from "@/services/contentEngine/contentLogger";
import DocsSkeleton from "@/pages/DocsSkeleton";

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

type AccessState = "loading" | "allowed" | "denied" | "no-user";

/**
 * Auth gate dla stron wymagających whitelist.
 *
 * Flow:
 * 1. Brak sesji → redirect /auth (loading state Skeleton)
 * 2. Sesja → wywołaj RPC `is_allowed_user()` (SECURITY DEFINER w Supabase)
 * 3. RPC zwraca true → render children
 * 4. RPC zwraca false → redirect / (dashboard)
 * 5. RPC error (404 not found) → fallback SELECT FROM allowed_users WHERE user_id = auth.uid()
 * 6. Wszystko inne (permission, network) → log error + deny by default
 *
 * NIE fallbackować na pełny dostęp przy błędzie. Deny-by-default.
 */
const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState<AccessState>("loading");

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setAccess("no-user");
      navigate("/auth");
      return;
    }

    let cancelled = false;

    async function checkWhitelist() {
      try {
        // Primary path: RPC SECURITY DEFINER
        const { data, error } = await supabase.rpc("is_allowed_user");

        if (cancelled) return;

        if (!error && data === true) {
          setAccess("allowed");
          return;
        }
        if (!error && data === false) {
          setAccess("denied");
          authLog.public("AdminProtectedRoute", `User ${user!.email} not in whitelist (RPC false)`);
          navigate("/");
          return;
        }

        // RPC error path
        if (error) {
          // 404 / function not exist → fallback do bezpośredniego SELECT
          const code = (error as { code?: string }).code;
          const isNotFound = code === "PGRST202" || /does not exist/i.test(error.message ?? "");

          if (isNotFound) {
            authLog.warn("AdminProtectedRoute", "is_allowed_user RPC not found, fallback to SELECT");
            const { data: row, error: selectError } = await supabase
              .from("allowed_users")
              .select("user_id")
              .eq("user_id", user!.id)
              .limit(1)
              .maybeSingle();

            if (cancelled) return;

            if (selectError) {
              authLog.error("AdminProtectedRoute", "Fallback SELECT allowed_users failed (deny)", selectError);
              setAccess("denied");
              navigate("/");
              return;
            }
            if (row) {
              setAccess("allowed");
              return;
            }
            setAccess("denied");
            navigate("/");
            return;
          }

          // Other error: deny by default + log
          authLog.error("AdminProtectedRoute", "is_allowed_user RPC fail (deny)", error);
          setAccess("denied");
          navigate("/");
          return;
        }
      } catch (e) {
        if (cancelled) return;
        authLog.error("AdminProtectedRoute", "Unexpected exception during whitelist check (deny)", e);
        setAccess("denied");
        navigate("/");
      }
    }

    checkWhitelist();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, navigate]);

  // Loading state (auth ładuje się LUB whitelist check w toku)
  if (authLoading || access === "loading") {
    return <DocsSkeleton />;
  }

  // Niezalogowany / odmowa → już zainicjowano navigate, render nic
  if (access !== "allowed") return null;

  return <>{children}</>;
};

export default AdminProtectedRoute;
